/**
 * Mega Menu Component - Tabbed Interface
 *
 * Full-width overlay with tabbed navigation.
 * Features:
 * - Tabbed interface showing one domain at a time
 * - Search/filter across all items
 * - Work items displayed as prominent cards with badges
 * - Keyboard navigation (arrows to switch tabs)
 */

import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { Link } from '@/lib/router';
import { Input, Badge } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useModel } from '@/context/AuthContext';
import { useMegaMenu } from '@/context/MegaMenuContext';
import { getMenuIcon } from '@/layouts/menuIcons';
import { isPermitted, type ModuleName, type PermissionVerbAction } from '@/components/Guard';
import { getAllDomains, type MegaMenuItem, type MegaMenuDomain } from './megaMenuConfig';
import styles from './MegaMenu.module.less';

// =============================================================================
// PERMISSION FILTERING
// =============================================================================

/**
 * Filter menu items based on user permissions and superadmin status.
 * Recursively filters children as well.
 */
function filterByPermission(items: MegaMenuItem[], acl: any, isSuperAdmin: boolean): MegaMenuItem[] {
  return items
    .map((item) => {
      if (item.superadminOnly && !isSuperAdmin) {
        return null;
      }

      // Check permission for this item
      if (item.acl) {
        const hasPermission = isPermitted(
          item.acl.module as ModuleName,
          item.acl.action as PermissionVerbAction,
          acl
        );
        if (!hasPermission) {
          return null;
        }
      }

      // If item has children, filter them too
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterByPermission(item.children, acl, isSuperAdmin);
        // If all children are filtered out, hide the parent too
        if (filteredChildren.length === 0) {
          return null;
        }
        return { ...item, children: filteredChildren };
      }

      return item;
    })
    .filter((item): item is MegaMenuItem => item !== null);
}

/**
 * Filter an entire domain's items by permission
 */
function filterDomainByPermission(domain: MegaMenuDomain, acl: any, isSuperAdmin: boolean): MegaMenuDomain {
  return {
    ...domain,
    items: filterByPermission(domain.items, acl, isSuperAdmin),
  };
}

// =============================================================================
// HIGHLIGHT TEXT
// =============================================================================

const HighlightText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query) return <>{text}</>;

  try {
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className={styles.highlight}>
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  } catch {
    return <>{text}</>;
  }
};

// =============================================================================
// WORK CARD - Special card layout for Work items
// =============================================================================

interface WorkCardProps {
  item: MegaMenuItem;
  badgeCount?: number;
  onNavigate: () => void;
}

const WorkCard: React.FC<WorkCardProps> = ({ item, badgeCount, onNavigate }) => {
  const hasBadge = badgeCount !== undefined && badgeCount > 0;
  const icon = item.icon ? getMenuIcon(item.icon) : null;

  return (
    <Link to={item.path} className={styles.workCard} onClick={onNavigate}>
      {icon && <span className={styles.workCardIcon}>{icon}</span>}
      <span className={styles.workCardLabel}>{item.label}</span>
      {hasBadge && (
        <span className={styles.workCardBadge}>{badgeCount > 999 ? '999+' : badgeCount}</span>
      )}
    </Link>
  );
};

// =============================================================================
// MENU ITEM - Regular item for other domains
// =============================================================================

interface MenuItemProps {
  item: MegaMenuItem;
  initialState: any;
  onNavigate: () => void;
  searchQuery?: string;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, initialState, onNavigate, searchQuery }) => {
  const hasChildren = item.children && item.children.length > 0;
  const badgeCount = item.badgeKey ? initialState?.[item.badgeKey] : undefined;

  if (hasChildren) {
    return (
      <li className={styles.menuItem}>
        <div className={styles.menuGroupHeader}>
          <span className={styles.menuLinkLabel}>
            <HighlightText text={item.label} query={searchQuery || ''} />
          </span>
          {badgeCount > 0 && <span className={styles.menuLinkBadge}>{badgeCount}</span>}
        </div>
        <ul className={styles.submenuExpanded}>
          {item.children?.map((child) => (
            <li key={child.path} className={styles.submenuItem}>
              <Link to={child.path} className={styles.submenuLink} onClick={onNavigate}>
                <HighlightText text={child.label} query={searchQuery || ''} />
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className={styles.menuItem}>
      <Link to={item.path} className={styles.menuLink} onClick={onNavigate}>
        <span className={styles.menuLinkLabel}>
          <HighlightText text={item.label} query={searchQuery || ''} />
        </span>
        {badgeCount > 0 && <span className={styles.menuLinkBadge}>{badgeCount}</span>}
      </Link>
    </li>
  );
};

// =============================================================================
// TAB BAR
// =============================================================================

interface TabBarProps {
  domains: MegaMenuDomain[];
  activeTab: string;
  onTabChange: (id: string) => void;
  initialState: any;
}

const TabBar: React.FC<TabBarProps> = ({ domains, activeTab, onTabChange, initialState }) => {
  // Calculate total pending count for Work tab
  const getTabBadge = (domain: MegaMenuDomain): number | undefined => {
    if (domain.id !== 'work') return undefined;

    let total = 0;
    for (const item of domain.items) {
      // Check direct badge on item
      if (item.badgeKey && initialState?.[item.badgeKey]) {
        total += initialState[item.badgeKey];
      }
      // Check badges on children (grouped items)
      if (item.children) {
        for (const child of item.children) {
          if (child.badgeKey && initialState?.[child.badgeKey]) {
            total += initialState[child.badgeKey];
          }
        }
      }
    }
    return total > 0 ? total : undefined;
  };

  return (
    <div className={styles.tabBar}>
      {domains.map((domain) => {
        const icon = getMenuIcon(domain.icon);
        const badge = getTabBadge(domain);
        const isActive = activeTab === domain.id;

        return (
          <button
            key={domain.id}
            type="button"
            className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
            onClick={() => onTabChange(domain.id)}
          >
            <span className={styles.tabIcon}>{icon}</span>
            <span className={styles.tabLabel}>{domain.title}</span>
            {badge !== undefined && (
              <Badge count={badge} overflowCount={999} className={styles.tabBadge} />
            )}
          </button>
        );
      })}
    </div>
  );
};

// =============================================================================
// DOMAIN CONTENT
// =============================================================================

interface DomainContentProps {
  domain: MegaMenuDomain;
  initialState: any;
  onNavigate: () => void;
  searchQuery?: string;
}

const DomainContent: React.FC<DomainContentProps> = ({ domain, initialState, onNavigate, searchQuery }) => {
  // Work domain gets special card treatment with grouped layout
  if (domain.id === 'work') {
    // Check if Work items are grouped (have children)
    const hasGroups = domain.items.some((item) => item.children && item.children.length > 0);

    if (hasGroups) {
      // Grouped Work layout: show groups with WorkCards for each child
      return (
        <div className={styles.workGroupedGrid}>
          {domain.items.map((group) => (
            <div key={group.path} className={styles.workGroup}>
              <div className={styles.workGroupHeader}>{group.label}</div>
              <div className={styles.workGroupItems}>
                {group.children?.map((child) => (
                  <WorkCard
                    key={child.path}
                    item={child}
                    badgeCount={child.badgeKey ? initialState?.[child.badgeKey] : undefined}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Flat Work layout (legacy)
    return (
      <div className={styles.workGrid}>
        {domain.items.map((item) => (
          <WorkCard
            key={item.path}
            item={item}
            badgeCount={item.badgeKey ? initialState?.[item.badgeKey] : undefined}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  }

  // Check if all items have children (grouped layout)
  const isGrouped = domain.items.every((item) => item.children && item.children.length > 0);

  if (isGrouped) {
    // Grouped layout: side-by-side columns
    return (
      <div className={styles.groupedGrid}>
        {domain.items.map((group) => (
          <div key={group.path} className={styles.groupColumn}>
            <div className={styles.groupHeader}>
              <HighlightText text={group.label} query={searchQuery || ''} />
            </div>
            <ul className={styles.groupItems}>
              {group.children?.map((child) => (
                <li key={child.path} className={styles.groupItem}>
                  <Link to={child.path} className={styles.groupItemLink} onClick={onNavigate}>
                    <HighlightText text={child.label} query={searchQuery || ''} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  // Mixed/flat layout: CSS columns
  return (
    <div className={styles.domainGrid}>
      <ul className={styles.itemList}>
        {domain.items.map((item) => (
          <MenuItem
            key={item.path}
            item={item}
            initialState={initialState}
            onNavigate={onNavigate}
            searchQuery={searchQuery}
          />
        ))}
      </ul>
    </div>
  );
};

// =============================================================================
// SEARCH RESULTS
// =============================================================================

interface SearchResultsProps {
  results: Array<{ domain: MegaMenuDomain; items: MegaMenuItem[] }>;
  initialState: any;
  onNavigate: () => void;
  searchQuery: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, initialState, onNavigate, searchQuery }) => {
  return (
    <div className={styles.searchResultsGrid}>
      {results.map(({ domain, items }) => (
        <div key={domain.id} className={styles.searchResultDomain}>
          <div className={styles.searchResultHeader}>
            <span className={styles.searchResultIcon}>{getMenuIcon(domain.icon)}</span>
            <span className={styles.searchResultTitle}>{domain.title}</span>
          </div>
          <ul className={styles.itemList}>
            {items.map((item) => (
              <MenuItem
                key={item.path}
                item={item}
                initialState={initialState}
                onNavigate={onNavigate}
                searchQuery={searchQuery}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// FILTER FUNCTION
// =============================================================================

const filterItems = (items: MegaMenuItem[], query: string): MegaMenuItem[] => {
  if (!query) return items;
  const lowerQuery = query.toLowerCase();

  return items.filter((item) => {
    if (item.label.toLowerCase().includes(lowerQuery)) return true;
    if (item.children?.some((child) => child.label.toLowerCase().includes(lowerQuery))) {
      return true;
    }
    return false;
  });
};

// =============================================================================
// MAIN MEGA MENU
// =============================================================================

export const MegaMenu: React.FC = () => {
  const { isOpen, close } = useMegaMenu();
  const { initialState } = useModel('@@initialState');
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<any>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('work');

  // Get user's ACL for permission filtering
  const userAcl = initialState?.currentUser?.acl;
  const isSuperAdmin = !initialState?.currentUser?.vendorId;

  // Get all domains filtered by user permissions
  const allDomains = useMemo(() => {
    const domains = getAllDomains();
    return domains
      .map((domain) => filterDomainByPermission(domain, userAcl, isSuperAdmin))
      .filter((domain) => domain.items.length > 0);
  }, [userAcl, isSuperAdmin]);

  // Get active domain
  const activeDomain = useMemo(
    () => allDomains.find((d) => d.id === activeTab) || allDomains[0],
    [allDomains, activeTab]
  );

  // Filter domains by search query
  const filteredResults = useMemo(() => {
    if (!searchQuery) return null;

    return allDomains
      .map((domain) => ({
        domain,
        items: filterItems(domain.items, searchQuery),
      }))
      .filter((d) => d.items.length > 0);
  }, [searchQuery, allDomains]);

  // Total filtered results count
  const totalResults = useMemo(() => {
    if (!filteredResults) return 0;
    return filteredResults.reduce((sum, d) => sum + d.items.length, 0);
  }, [filteredResults]);

  // Handlers
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        close();
        setSearchQuery('');
      }
    },
    [close]
  );

  const handleNavigate = useCallback(() => {
    close();
    setSearchQuery('');
  }, [close]);

  const handleClose = useCallback(() => {
    close();
    setSearchQuery('');
  }, [close]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  const handleTabChange = useCallback((id: string) => {
    setActiveTab(id);
  }, []);

  // Focus search when opened, reset when closed
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Keyboard navigation for tabs
  useEffect(() => {
    if (!isOpen || searchQuery) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const currentIndex = allDomains.findIndex((d) => d.id === activeTab);

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % allDomains.length;
        setActiveTab(allDomains[nextIndex].id);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + allDomains.length) % allDomains.length;
        setActiveTab(allDomains[prevIndex].id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, searchQuery, activeTab, allDomains]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const isSearching = searchQuery.length > 0;

  return (
    <>
      <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} onClick={handleOverlayClick} />

      <div ref={panelRef} className={`${styles.menuPanel} ${isOpen ? styles.open : ''}`}>
        {/* Search header */}
        <div className={styles.searchHeader}>
          <div className={styles.searchContainer}>
            <Input
              ref={searchInputRef}
              placeholder="Search menu items..."
              prefix={<SearchOutlined className={styles.searchIcon} />}
              suffix={
                searchQuery ? (
                  <CloseOutlined className={styles.clearIcon} onClick={handleClearSearch} />
                ) : null
              }
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
              allowClear={false}
            />
            {isSearching && (
              <span className={styles.searchResultCount}>
                {totalResults} {totalResults === 1 ? 'result' : 'results'}
              </span>
            )}
          </div>
          <div className={styles.headerRight}>
            <span className={styles.previewTag}>Preview</span>
            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close menu">
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Tab bar - hidden when searching or when no domains available */}
        {!isSearching && allDomains.length > 0 && (
          <TabBar
            domains={allDomains}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            initialState={initialState}
          />
        )}

        {/* Content area */}
        <div className={styles.menuContent}>
          {isSearching ? (
            filteredResults && filteredResults.length > 0 ? (
              <SearchResults
                results={filteredResults}
                initialState={initialState}
                onNavigate={handleNavigate}
                searchQuery={searchQuery}
              />
            ) : (
              <div className={styles.noResults}>
                <p>No menu items match "{searchQuery}"</p>
                <button type="button" onClick={handleClearSearch} className={styles.clearButton}>
                  Clear search
                </button>
              </div>
            )
          ) : activeDomain ? (
            <DomainContent
              domain={activeDomain}
              initialState={initialState}
              onNavigate={handleNavigate}
            />
          ) : (
            <div className={styles.noResults}>
              <p>No menu items available</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MegaMenu;
